import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Ico } from "./malluIcons";
import { MALLU_MARK_SRC } from "./malluMark";
import { MalluCardMock } from "./MalluCardMock";
import { CTA_RIBBONS, useRibbon, useTilt } from "./malluMotion";

const asList = (value) => (Array.isArray(value) ? value : []);
const asObj = (value) => (value && typeof value === "object" ? value : {});

const CARD_ICONS = ["checkCircle", "phone", "globe", "nfc"];
const SEC_ICONS = ["scan", "lock", "clock", "bolt"];
const STEP_ICONS = ["scan", "phone", "lock", "clock"];
const BIZ_ICONS = ["layers", "code", "users", "sync"];
const BARS = [38, 52, 44, 66, 58, 74, 62, 88, 70, 92, 84, 100];

/** Eyebrow + título + parágrafo, com reveal escalonado. */
const SectionHead = ({ eyebrow, title, lead, tone = "dark", leadClass = "" }) => {
  const light = tone === "light";
  return (
    <>
      <div className={`pp-eyebrow ${light ? "pp-eyebrow--cyan" : "pp-eyebrow--blue"} rv rv-y20`}>
        {eyebrow}
      </div>
      <h2 className={`pp-h2 ${light ? "pp-h2--light" : "pp-h2--dark"} rv rv-y28`} data-delay="100">
        {title}
      </h2>
      <p
        className={`pp-lead ${light ? "pp-lead--light" : "pp-lead--dark"} ${leadClass} rv rv-y24`}
        data-delay="200"
      >
        {lead}
      </p>
    </>
  );
};

/** Cards brancos de benefícios (seções Cartão e Segurança). */
const FeatureCards = ({ items, icons, start = 250, step = 100 }) => (
  <div className="pp-cards">
    {items.map((item, i) => (
      <div className="pp-card rv rv-y28" data-delay={start + i * step} key={item.title}>
        <div className="pp-card-ico">
          <Ico name={icons[i] || "checkCircle"} />
        </div>
        <div className="pp-card-title">{item.title}</div>
        <div className="pp-card-text">{item.text}</div>
      </div>
    ))}
  </div>
);

/* ---------------------------------------------------------------- valores */
export const MalluValues = () => {
  const { t } = useTranslation("malluLanding");
  const values = asList(t("values", { returnObjects: true }));

  const group = (hidden) => (
    <div className="pp-marquee-group" aria-hidden={hidden ? "true" : undefined}>
      {values.map((v) => (
        <React.Fragment key={v.title}>
          <span className="pp-marquee-item">
            <strong>{v.title}</strong>
            <span>{v.text}</span>
          </span>
          <span className="pp-marquee-dot" />
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="pp-marquee">
      <div className="pp-marquee-fade" />
      <div className="pp-marquee-track">
        {group(false)}
        {group(true)}
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------- cartão */
export const MalluCard = ({ motion }) => {
  const { t } = useTranslation("malluLanding");
  const cardRef = useRef(null);
  const tilt = useTilt(cardRef, { max: 22, enabled: motion > 0 });
  const features = asList(t("card.features", { returnObjects: true }));

  return (
    <section
      id="cartao"
      className="pp-section pp-section--light"
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
    >
      <div className="pp-wrap pp-grid2">
        <div className="pp-card-stage rv rv-y50">
          <div className="pp-card-halo" aria-hidden="true" />
          <div ref={cardRef} className="pp-card-tilt" aria-hidden="true">
            <div className="pp-card-ghost" aria-hidden="true">
              <img src={MALLU_MARK_SRC} alt="" />
            </div>
            <MalluCardMock t={t} large />
          </div>
        </div>
        <div>
          <SectionHead
            eyebrow={t("card.eyebrow")}
            title={t("card.title")}
            lead={t("card.lead")}
            tone="dark"
          />
          <FeatureCards items={features} icons={CARD_ICONS} />
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------- pix */
export const MalluPix = () => {
  const { t } = useTranslation("malluLanding");
  const features = asList(t("pix.features", { returnObjects: true }));
  const m = asObj(t("pix.mock", { returnObjects: true }));

  return (
    <section id="pix" className="pp-section pp-section--dark">
      <div className="pp-pix-glow" aria-hidden="true" />
      <div className="pp-wrap pp-grid2">
        <div>
          <SectionHead
            eyebrow={t("pix.eyebrow")}
            title={t("pix.title")}
            lead={t("pix.lead")}
            tone="light"
          />
          <div className="pp-list">
            {features.map((f, i) => (
              <div className="pp-list-item rv rv-y24" data-delay={250 + i * 80} key={f.title}>
                <div className="pp-list-check">
                  <Ico name="check" size={16} color="#ff68b5" sw={2.4} />
                </div>
                <div>
                  <div className="pp-list-title">{f.title}</div>
                  <div className="pp-list-text">{f.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pp-pix-stage rv rv-y50" data-delay="200">
          <div className="pp-pix-halo" aria-hidden="true" />
          <div className="pp-glass" aria-hidden="true">
            <div className="pp-glass-head">
              <div className="pp-glass-title">{m.title}</div>
              <div className="pp-live">
                <span className="pp-live-dot" />
                {m.badge}
              </div>
            </div>
            <div className="pp-pix-to">
              <div className="pp-avatar">{m.initials}</div>
              <div>
                <div className="pp-pix-name">{m.name}</div>
                <div className="pp-pix-detail">{m.detail}</div>
              </div>
            </div>
            <div className="pp-pix-body">
              <div className="pp-label-sm">{m.amountLabel}</div>
              <div className="pp-pix-amount">{m.amount}</div>
              <div className="pp-pix-row">
                <span>{m.keyLabel}</span>
                <span>{m.key}</span>
              </div>
              <div className="pp-pix-row">
                <span>{m.feeLabel}</span>
                <span className="is-cyan">{m.fee}</span>
              </div>
            </div>
            <div className="pp-pix-confirm">
              <div className="pp-pix-bar" />
              <span>{m.confirm}</span>
            </div>
            <div className="pp-done-pill pp-done-pill--pix">
              <Ico name="check" size={16} sw={2.6} />
              {m.done}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------- segurança */
export const MalluSecurity = () => {
  const { t } = useTranslation("malluLanding");
  const features = asList(t("security.features", { returnObjects: true }));
  const m = asObj(t("security.mock", { returnObjects: true }));
  const steps = asList(m.steps);

  return (
    <section id="seguranca" className="pp-section pp-section--light">
      <div className="pp-wrap pp-grid2">
        <div className="pp-sec-stage rv rv-y50" data-delay="200">
          <div className="pp-card-halo" aria-hidden="true" />
          <div className="pp-sec-panel" aria-hidden="true">
            <div className="pp-sec-mark">
              <img src={MALLU_MARK_SRC} alt="" />
            </div>
            <div className="pp-sec-head">
              <div className="pp-sec-head-l">
                <div className="pp-ico-cyan">
                  <Ico name="shield" size={18} color="#ff68b5" sw={1.9} />
                </div>
                <div className="pp-sec-title">{m.title}</div>
              </div>
              <div className="pp-live">
                <span className="pp-live-dot" />
                {m.badge}
              </div>
            </div>
            <div className="pp-sec-tx">
              <div className="pp-scan" />
              <div className="pp-sec-tx-ico">
                <Ico name="pix" size={20} color="#ff68b5" sw={2} />
              </div>
              <div className="pp-sec-tx-body">
                <div className="pp-sec-tx-title">{m.txTitle}</div>
                <div className="pp-sec-tx-meta">{m.txMeta}</div>
              </div>
              <div className="pp-sec-tx-amt">{m.txAmount}</div>
            </div>
            <div className="pp-steps">
              <div className="pp-steps-line" />
              {steps.map((s, i) => (
                <div className={`pp-step pp-step--${i + 1} rv rv-x24`} data-delay={300 + i * 80} key={s.title}>
                  <div className="pp-step-bg" />
                  <div className="pp-step-dot">
                    <div className="pp-step-check">
                      <Ico name="check" size={16} color="#13102e" sw={3} />
                    </div>
                    <Ico name={STEP_ICONS[i] || "scan"} size={15} color="#9d94bc" />
                  </div>
                  <div className="pp-step-body">
                    <div className="pp-step-title">{s.title}</div>
                    <div className="pp-step-meta">{s.text}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="pp-done-anchor">
              <div className="pp-done-pill pp-done-pill--sec">
                <Ico name="shield" size={16} sw={2.6} />
                {m.done}
              </div>
            </div>
          </div>
        </div>

        <div>
          <SectionHead
            eyebrow={t("security.eyebrow")}
            title={t("security.title")}
            lead={t("security.lead")}
            tone="dark"
          />
          <FeatureCards items={features} icons={SEC_ICONS} step={80} />
        </div>
      </div>
    </section>
  );
};

/* --------------------------------------------------------------- empresas */
export const MalluBusiness = ({ urls }) => {
  const { t } = useTranslation("malluLanding");
  const features = asList(t("business.features", { returnObjects: true }));
  const m = asObj(t("business.mock", { returnObjects: true }));
  const axis = asList(m.axis);

  return (
    <section id="empresas" className="pp-section pp-section--deep">
      <div className="pp-biz-glow" aria-hidden="true" />
      <div className="pp-wrap pp-grid2">
        <div>
          <SectionHead
            eyebrow={t("business.eyebrow")}
            title={t("business.title")}
            lead={t("business.lead")}
            tone="light"
            leadClass="pp-lead--tight"
          />
          <div className="pp-biz-feats">
            {features.map((label, i) => (
              <div className="pp-biz-feat rv rv-y20" data-delay={250 + i * 70} key={label}>
                <span className="pp-biz-feat-ico">
                  <Ico name={BIZ_ICONS[i] || "layers"} size={18} />
                </span>
                {label}
              </div>
            ))}
          </div>
          <div className="pp-ctas rv rv-y20" data-delay="520">
            <a
              className="pp-btn pp-btn--primary pp-btn--md"
              href={urls.register}
              target="_blank"
              rel="noreferrer"
            >
              {t("business.ctaPrimary")} <Ico name="arrow" size={18} sw={2.2} />
            </a>
            <Link className="pp-btn pp-btn--ghost pp-btn--md" to="/message">
              {t("business.ctaSecondary")}
            </Link>
          </div>
        </div>

        <div className="rv rv-y50" data-delay="200">
          <div className="pp-dash" aria-hidden="true">
            <div className="pp-dash-head">
              <div>
                <div className="pp-label-sm">{m.label}</div>
                <div className="pp-dash-amount">{m.amount}</div>
                <div className="pp-dash-period">{m.period}</div>
              </div>
              <div className="pp-pill-up">
                <Ico name="trend" size={14} sw={2.4} />
                {m.growth}
              </div>
            </div>
            <div className="pp-bars">
              {BARS.map((h, i) => (
                <div
                  className="pp-bar rv rv-bar"
                  data-delay={300 + i * 60}
                  style={{ height: `${h}%` }}
                  key={i}
                />
              ))}
            </div>
            <div className="pp-axis">
              {axis.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
            <div className="pp-dash-stats">
              <div>
                <div className="pp-stat-label">{m.stat1Label}</div>
                <div className="pp-stat-val">{m.stat1}</div>
              </div>
              <div>
                <div className="pp-stat-label">{m.stat2Label}</div>
                <div className="pp-stat-val">{m.stat2}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------- faq */
export const MalluFaq = () => {
  const { t } = useTranslation("malluLanding");
  const [open, setOpen] = useState(0);
  const items = asList(t("faq.items", { returnObjects: true }));

  return (
    <section id="faq" className="pp-section pp-section--light">
      <div className="pp-faq-wrap">
        <div className="pp-faq-head">
          <div className="pp-eyebrow pp-eyebrow--blue rv rv-y20">{t("faq.eyebrow")}</div>
          <h2 className="pp-h2 pp-h2--dark rv rv-y28" data-delay="100">
            {t("faq.title")}
          </h2>
        </div>
        <div className="pp-faq-list">
          {items.map((item, i) => {
            const isOpen = open === i;
            const panelId = `pp-faq-panel-${i}`;
            return (
              <div className={`pp-faq rv rv-y24${isOpen ? " is-open" : ""}`} data-delay={150 + i * 70} key={item.q}>
                <button
                  type="button"
                  className="pp-faq-q"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                >
                  <span>{item.q}</span>
                  <span className="pp-faq-plus" aria-hidden="true">
                    <Ico name="plus" size={16} sw={2.4} />
                  </span>
                </button>
                <div className="pp-faq-a" id={panelId} aria-hidden={!isOpen}>
                  <div>
                    <p>{item.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------ download app */
export const MalluApp = ({ urls, motion }) => {
  const { t } = useTranslation("malluLanding");
  const canvasRef = useRef(null);
  useRibbon(canvasRef, CTA_RIBBONS, motion);

  return (
    <section id="aplicativo" className="pp-section pp-section--deep pp-app-sec">
      <canvas ref={canvasRef} className="pp-canvas" aria-hidden="true" />
      <div className="pp-app-veil" aria-hidden="true" />
      <div className="pp-app-inner">
        <div className="pp-app-icon-wrap rv rv-scale">
          <div className="pp-app-icon-glow" aria-hidden="true" />
          <div className="pp-app-icon">
            <img src={MALLU_MARK_SRC} alt="Mallu Pay" />
          </div>
        </div>
        <div className="pp-eyebrow pp-eyebrow--cyan rv rv-y20" data-delay="100">
          {t("app.eyebrow")}
        </div>
        <h2 className="pp-app-title rv rv-y28" data-delay="200">
          {t("app.title")}
        </h2>
        <p className="pp-lead pp-lead--light pp-app-lead rv rv-y24" data-delay="300">
          {t("app.lead")}
        </p>
        <div className="pp-stores rv rv-y24" data-delay="400">
          <a className="pp-store" href={urls.appStore} target="_blank" rel="noreferrer">
            <Ico name="phoneWide" size={24} />
            <span className="pp-store-txt">
              <span className="pp-store-top">{t("app.appStoreTop")}</span>
              <span className="pp-store-name">App Store</span>
            </span>
          </a>
          <a className="pp-store" href={urls.googlePlay} target="_blank" rel="noreferrer">
            <Ico name="play" size={24} />
            <span className="pp-store-txt">
              <span className="pp-store-top">{t("app.playTop")}</span>
              <span className="pp-store-name">Google Play</span>
            </span>
          </a>
        </div>
        <div className="pp-app-note rv rv-y16" data-delay="500">
          {t("app.note")}
        </div>
      </div>
    </section>
  );
};
